import { Component, Input } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { select, Store } from '@ngrx/store';
import * as EmployeeActions from "../../../stores/global/employees/employee-actions";
import { combineLatest, map, Observable, tap } from 'rxjs';
import { PermissionStorageService } from 'src/app/authentication/storage/permission-storage.service';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { DeviceNoteStore } from 'src/app/stores/component/device-note-store';
import { ICreateNoteRequest, INote, INoteDetailsResponse, IUpdateNoteRequest } from '../../models/note';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';

@Component({
    selector: 'app-device-detail-notes',
    templateUrl: './device-detail-notes.component.html',
    styleUrl: './device-detail-notes.component.scss',
    standalone: false
})
export class DeviceDetailNotesComponent {
  @Input() isAdmin;
  @Input() deviceId;
  
  vm$: Observable<any>;
  public noteForm: UntypedFormGroup;
  editingNote: INoteDetailsResponse | null = null;
  notesOpened = false;
  currentUserId: string;

  constructor(
    private store: Store<AppStateInterface>,
    private translocoService: TranslocoService,
    private fb: FormBuilder,
    private deviceNoteStore: DeviceNoteStore,
  ) { 
    this.vm$ = combineLatest([ 
      this.deviceNoteStore.deviceNotes$,
      this.store.pipe(select(currentEmployeeSelector))
    ]).pipe(
      map(([
        notes,
        currentEmployee
      ]) => ({
        notes,
        currentEmployee
      })),
      tap(({ currentEmployee }) => this.patchUserData(currentEmployee))
    );

    this.noteForm = this.fb.group({
      description: new UntypedFormControl(null, {
        validators: Validators.required
      }),
      createdOn: [new Date().toISOString()],
      createdBy: [this.currentUserId]
    });
  }

  ngOnInit() {
    this.deviceNoteStore.getNotesByDevice(this.deviceId);
    this.store.dispatch(EmployeeActions.getCurrentEmployee());
  }

  patchUserData(employee) {
    this.currentUserId = employee.userId;
  }

  isCreator(note: INote): boolean {
  
    // If the note is new (no creator yet) or the current user is the creator, allow editing
    if (!note.createdBy || note.createdBy === this.currentUserId) {
      return true;
    }
  
    return false;
  }
  
  editNote(note: INoteDetailsResponse): void {
    this.openNoteDialog(note);
  }

  removeNote(): void {
    this.deviceNoteStore.removeNoteById(this.editingNote.id);
    this.closeNoteDialog('no');
  }

  openNoteDialog(note?: INoteDetailsResponse): void {
    this.editingNote = note || null;

    if (note) {
      // Populate the form with existing note data for editing
      this.noteForm.patchValue({
        description: note.description,
        createdOn: note.createdOn,
        createdBy: note.createdBy
      });
    } else {
      // Reset form for creating a new note
      this.noteForm.reset({
        createdOn: new Date().toISOString(),
        createdBy: this.currentUserId  // Set current user ID if needed
      });
    }

    this.notesOpened = true;
  }

  public closeNoteDialog(status: string): void {
    this.notesOpened = false;
    this.noteForm.reset();
  }

  submitNoteCreation(): void {
    if (this.editingNote) {
      // Edit existing note
      const updateRequest: IUpdateNoteRequest = {
        id: this.editingNote.id,
        description: this.noteForm.value.description
      };
      this.deviceNoteStore.updateNoteById(updateRequest);
    } else {
      // Create new note
      const request: ICreateNoteRequest = {
        description: this.noteForm.value.description,
        deviceId: this.deviceId,
        userId: this.currentUserId
      };
      this.deviceNoteStore.addNote(request);
    }

    this.closeNoteDialog('no');
  }

  sanitizeCreationDate(inputdate: any) {
    if (!inputdate) {
      return "-";
    }

    const date = new Date(inputdate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }
}
